import React from 'react'
import { AlertTriangle, RefreshCw } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import { useQualificationNotificationSettings } from '../hooks/useQualificationNotificationSettings'
import type { QualificationNotificationMode } from '../types'

type Props = {
  workspaceId: string
  role: 'owner' | 'admin' | 'member'
}

const MODE_OPTIONS: { value: QualificationNotificationMode; label: string }[] = [
  { value: 'all', label: 'All results' },
  { value: 'qualified_only', label: 'Qualified only' },
  { value: 'unqualified_only', label: 'Unqualified only' },
  { value: 'off', label: 'Off' },
]

// Same segmented-control look as crm-permissions' DecisionToggle, but generic
// over this feature's own 4-value mode instead of that feature's fixed
// auto/approval/disabled enum — kept local rather than shared to avoid coupling
// two independently-owned features to one component (CLAUDE.md: keep feature
// ownership clear).
const ModeToggle: React.FC<{
  value: QualificationNotificationMode
  onChange: (value: QualificationNotificationMode) => void
  disabled?: boolean
}> = ({ value, onChange, disabled }) => {
  const { currentTheme: t } = useTheme()

  return (
    <div
      className="inline-flex flex-wrap items-center gap-1 rounded-lg p-1"
      style={{ backgroundColor: t.sectionBackground, border: `1px solid ${t.border}` }}
    >
      {MODE_OPTIONS.map((opt) => {
        const isActive = value === opt.value
        return (
          <button
            key={opt.value}
            type="button"
            disabled={disabled}
            onClick={() => onChange(opt.value)}
            className="h-7 px-2.5 rounded-md text-[11.5px] font-medium transition-colors whitespace-nowrap disabled:cursor-not-allowed disabled:opacity-60"
            style={{
              backgroundColor: isActive ? (opt.value === 'off' ? t.errorBackground : t.accent) : 'transparent',
              color: isActive ? (opt.value === 'off' ? t.errorText : t.buttonText) : t.secondrytext,
            }}
          >
            {opt.label}
          </button>
        )
      })}
    </div>
  )
}

const QualificationNotificationSettingsSection: React.FC<Props> = ({ workspaceId, role }) => {
  const { currentTheme: t } = useTheme()
  const canManage = role === 'owner' || role === 'admin'
  const { settings, loading, error, retry, saving, saveError, setMode } = useQualificationNotificationSettings(
    workspaceId,
    canManage,
  )

  if (!canManage) {
    return (
      <div className="flex flex-col gap-3">
        <p className="text-[12.5px]" style={{ color: t.mutedText }}>
          Only the workspace owner or an admin can view or change qualification notifications.
        </p>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6">
      {loading && (
        <div className="h-16 rounded-xl animate-pulse" style={{ backgroundColor: t.hoverBackground }} />
      )}

      {!loading && (error || !settings) && (
        <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
          <div
            className="flex h-14 w-14 items-center justify-center rounded-xl"
            style={{ backgroundColor: t.errorBackground }}
          >
            <AlertTriangle size={24} style={{ color: t.errorText }} />
          </div>
          <div>
            <p className="font-semibold text-base" style={{ color: t.text }}>Couldn&apos;t load notification settings</p>
            <p className="text-sm mt-1" style={{ color: t.mutedText }}>{error || 'Something went wrong.'}</p>
          </div>
          <button
            type="button"
            onClick={retry}
            className="flex items-center gap-1.5 h-9 px-3.5 rounded-lg text-sm font-medium transition-colors"
            style={{ backgroundColor: t.hoverBackground, color: t.text, border: `1px solid ${t.border}` }}
          >
            <RefreshCw size={14} />
            Retry
          </button>
        </div>
      )}

      {!loading && !error && settings && (
        <>
          <p className="text-[12.5px]" style={{ color: t.mutedText }}>
            When Zeph finishes qualifying a lead — on demand or automatically when a new HubSpot contact comes
            in — this controls whether the lead's owner gets notified, and for which outcome.
          </p>

          <div
            className="rounded-xl p-4 flex flex-col gap-3"
            style={{ backgroundColor: t.sectionBackground, border: `1px solid ${t.border}` }}
          >
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                <label className="text-sm font-semibold block" style={{ color: t.text }}>
                  Qualification result notifications
                </label>
                <p className="text-[12.5px] mt-0.5" style={{ color: t.mutedText }}>
                  &ldquo;All results&rdquo; notifies for both qualified and unqualified leads.
                </p>
              </div>
              <ModeToggle value={settings.mode} onChange={(mode) => void setMode(mode)} disabled={saving} />
            </div>
            {saving && (
              <span className="text-[12px]" style={{ color: t.mutedText }}>Saving&hellip;</span>
            )}
            {saveError && (
              <span className="text-[12px]" style={{ color: t.errorText }}>{saveError}</span>
            )}
          </div>
        </>
      )}
    </div>
  )
}

export default React.memo(QualificationNotificationSettingsSection)
